import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Loader2, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { API } from "../api";
import { Modal, field, label } from "./Modal";

export const ReviewModal = () => {
  const { t } = useLang();
  const r = t.review;
  const { reviewRef, closeReview } = useFlow();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("idle");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (reviewRef) {
      setRating(0); setComment(""); setStatus("idle"); setError(""); setInfo(null);
      axios.get(`${API}/requests/${reviewRef}/review-info`)
        .then(({ data }) => { setInfo(data); if (data.already_reviewed) setStatus("done"); })
        .catch(() => setError(r.notfound));
    }
  }, [reviewRef, r.notfound]);

  const submit = async () => {
    if (!rating) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/reviews`, { request_ref: reviewRef, rating, comment });
      setStatus("done");
    } catch (e) {
      setStatus("idle");
      setError(e?.response?.status === 409 ? r.already : r.notfound);
    }
  };

  return (
    <Modal open={!!reviewRef} onClose={closeReview} testid="review-modal">
      <div className="p-8 md:p-10 text-center">
        {status === "done" ? (
          <>
            <div className="w-16 h-16 rounded-full bg-forest text-white flex items-center justify-center mx-auto"><Check size={30} strokeWidth={3} /></div>
            <h3 className="mt-5 font-heading text-2xl font-medium text-ink">{r.thanks}</h3>
            <button onClick={closeReview} className="mt-6 rounded-full border border-line px-6 py-3 font-semibold text-ink hover:bg-cream" data-testid="review-close">{r.close}</button>
          </>
        ) : (
          <>
            <h3 className="font-heading text-2xl font-medium text-ink">{r.title}</h3>
            <p className="mt-2 text-ink-soft">{r.intro} {reviewRef}?</p>
            <div className="mt-6 flex justify-center gap-2" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
                  <Star size={36} className={`transition-colors ${(hover || rating) >= n ? "fill-olive text-olive" : "text-line"}`} />
                </button>
              ))}
            </div>
            <div className="mt-6 text-left">
              <label className={label}>{r.comment}</label>
              <textarea rows={3} className={field} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <button onClick={submit} disabled={!rating || status === "loading"} className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-3.5 hover:bg-sage-deep transition-all disabled:opacity-50" data-testid="review-submit">
              {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> {r.submitting}</> : r.submit}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};
